import { create } from 'zustand';
import type { AdminAttentionDto, AdminWorkspaceDto } from '@ecom/contracts';
import { adminApi, withSession } from '@/lib/api-client';

/**
 * The shell's own state (docs/07-Admin/Admin-App.md): whose admin this is —
 * the store's name, logo and where its shop answers — and what is waiting on
 * the team, for the menu's badges and the top bar's list.
 *
 * Attention is read when the shell opens, again once a minute and whenever
 * the window comes back into focus, and after anything that changes it (an
 * order moved, a review published) — the badge saying "3" after the third was
 * handled is how a counter stops being trusted.
 */
interface WorkspaceState {
  workspace: AdminWorkspaceDto | null;
  attention: AdminAttentionDto;
  loadWorkspace: () => Promise<void>;
  refreshAttention: () => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  workspace: null,
  attention: {},

  async loadWorkspace() {
    try {
      const workspace = await withSession(() => adminApi.shell.workspace());
      set({ workspace });
    } catch {
      // The menu falls back to the admin's own name; nothing here is worth an error.
    }
  },

  async refreshAttention() {
    try {
      const attention = await withSession(() => adminApi.shell.attention());
      set({ attention });
    } catch {
      // A badge that did not refresh keeps its last number rather than vanishing.
    }
  },
}));

/** How often the counts are read while the admin is open. */
export const ATTENTION_INTERVAL_MS = 60_000;
